import React, { useCallback, useEffect, useRef, useState } from "react"
import axios from "axios"
import { useForm } from "react-hook-form"
import {
  NewsletterOfferService,
  type NewsletterOfferBroadcastJob,
} from "services/api-workers-service"
import { SentryService } from "services/SentryService"
import Button from "ui/components/atoms/button"
import Input from "ui/components/atoms/input"
import Accordion from "ui/components/molecules/accordion"
import Alert from "ui/components/molecules/alert"

type FormValues = { offerId: string }

type FastApiErrorBody = { detail?: string }

const POLL_INTERVAL_MS = 3000
const MAX_POLL_FAILURES = 5
const MAX_POLLS = 200

const parseDetail = (e: unknown): string | undefined => {
  if (!axios.isAxiosError(e)) return undefined
  const data = e.response?.data as FastApiErrorBody | undefined
  if (data && typeof data.detail === "string") return data.detail
  return undefined
}

const ToolNewsletterOfferBroadcast = () => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { offerId: "" },
  })

  const [job, setJob] = useState<NewsletterOfferBroadcastJob | null>(null)
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)
  const pollFailuresRef = useRef(0)
  const pollCountRef = useRef(0)

  useEffect(() => {
    if (!activeJobId) return undefined

    let cancelled = false
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const poll = async () => {
      try {
        const data = await NewsletterOfferService.getBroadcastJob(activeJobId)
        if (cancelled) return
        pollFailuresRef.current = 0
        setJob(data)
        if (data.status !== "running") {
          setActiveJobId(null)
          return
        }
      } catch (e) {
        if (cancelled) return
        pollFailuresRef.current += 1
        if (pollFailuresRef.current >= MAX_POLL_FAILURES) {
          SentryService.error("[ERROR NewsletterOfferService.getBroadcastJob]:", e)
          setErrorMessage(
            "Nie udało się pobrać statusu wysyłki — wysyłka trwa w tle, sprawdź logi workera.",
          )
          setActiveJobId(null)
          return
        }
      }
      pollCountRef.current += 1
      if (pollCountRef.current >= MAX_POLLS) {
        setErrorMessage(
          "Przekroczono limit oczekiwania na status — wysyłka trwa w tle, sprawdź logi workera.",
        )
        setActiveJobId(null)
        return
      }
      timeoutId = setTimeout(poll, POLL_INTERVAL_MS)
    }

    poll()

    return () => {
      cancelled = true
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [activeJobId])

  const onSubmit = useCallback(
    async ({ offerId }: FormValues) => {
      const trimmed = offerId.trim()
      if (
        !window.confirm(
          `Wysłać ofertę (ID: ${trimmed}) do użytkowników z widoku newsletterowego w promieniu lokalizacji oferty?`,
        )
      ) {
        return
      }
      setStarting(true)
      setJob(null)
      setErrorMessage(null)
      pollFailuresRef.current = 0
      pollCountRef.current = 0
      try {
        const data = await NewsletterOfferService.broadcastOffer(trimmed)
        setActiveJobId(data.job_id)
        reset()
      } catch (e) {
        SentryService.error("[ERROR NewsletterOfferService.broadcastOffer]:", e)
        setErrorMessage(
          parseDetail(e) ??
            "Nie udało się uruchomić wysyłki. Sprawdź ID oferty, klucz API i adres workera.",
        )
      } finally {
        setStarting(false)
      }
    },
    [reset],
  )

  const inProgress = activeJobId != null

  return (
    <Accordion title="Newsletter: wyślij ofertę">
      {inProgress && (
        <Alert $type="warning" $small $marginBottom="S">
          Wysyłka oferty{job ? <strong> {job.offer_id}</strong> : null} w toku… Możesz zostawić tę
          zakładkę otwartą, statystyki pojawią się po zakończeniu.
        </Alert>
      )}
      {!inProgress && job?.status === "finished" && job.stats && (
        <Alert $type="success" $small $marginBottom="S">
          Oferta <strong>{job.offer_id}</strong>: wysłano {job.stats.sent}, niepowodzenia{" "}
          {job.stats.failed}, pominięci {job.stats.skipped}, poza promieniem{" "}
          {job.stats.out_of_radius}.
        </Alert>
      )}
      {!inProgress && job?.status === "failed" && (
        <Alert $type="error" $small $marginBottom="S">
          Wysyłka oferty <strong>{job.offer_id}</strong> nie powiodła się
          {job.error ? <>: {job.error}</> : "."}
        </Alert>
      )}
      {errorMessage && (
        <Alert $type="error" $small $marginBottom="S">
          {errorMessage}
        </Alert>
      )}
      <Input
        name="offerId"
        label="ID oferty"
        error={errors.offerId}
        value={watch("offerId")}
        rhf={register("offerId", {
          required: "Podaj ID oferty",
          validate: v => (v != null && v.trim().length > 0) || "ID oferty nie może być puste",
        })}
        $marginBottom="S"
      />
      <Button
        $marginLeft="auto"
        icon="email"
        loading={starting || inProgress}
        onClick={handleSubmit(onSubmit)}
      >
        Wyślij ofertę
      </Button>
    </Accordion>
  )
}

export default ToolNewsletterOfferBroadcast
